// study-import.component.ts
import { Component, OnInit } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { NgForOf, NgIf, UpperCasePipe } from '@angular/common';
import { ImportStudyContext, MapItem } from '../import-study-context';
import { VariableService } from '../../../../../shared/ontology/service/variable.service';
import { VariableFilterRequest } from '../../../../../shared/ontology/model/variable-filter-request';
import { StudyVariableSelectModalComponent } from '../../../../shared/modals/variable-select-modal/study-variable-select-modal.component';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalConfirmComponent } from '../../../../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { DatasetService } from '../../../../../shared/dataset/service/dataset.service';
import { StudyEditorContext } from '../../../../study-editor.context';
import { VariableTypeEnum } from '../../../../../shared/ontology/variable-type.enum';
import { ExperimentalDesignContext } from '../../../experimental-design/experimental-design-context';
import { ObservationVariableItem } from '../../../../shared/models/observation-variable-item.model';

@Component({
    selector: 'jhi-study-import',
    templateUrl: './import-study-map.component.html',
    styleUrls: ['./import-study-map.component.scss']
})
export class ImportStudyMapComponent implements OnInit {

    unmappedItems: MapItem[] = [];
    mapItems: MapItem[] = [];
    maintainHeaders = false;
    isSaving: boolean;
    groupName: string;

    constructor(private studyEditorContext: StudyEditorContext,
                private importStudyContext: ImportStudyContext,
                private experimentalDesignContext: ExperimentalDesignContext,
                private datasetService: DatasetService,
                private variableService: VariableService,
                private variable: ImportStudyContext,
                private modalService: NgbModal,
                private activeModal: NgbActiveModal,
                private alertService: AlertService,
                private translateService: TranslateService) {

    }

    ngOnInit(): void {
        // Initialize with sample data
        this.unmappedItems = this.importStudyContext.unmappedItems;

        this.mapItems = [];

        const filterRequest = <VariableFilterRequest>({});
        if (this.importStudyContext.variableTypeIds && this.importStudyContext.variableTypeIds.length) {
            filterRequest.variableTypeIds = this.importStudyContext.variableTypeIds.map((enumType) => enumType.toString());
        }
        if (this.unmappedItems && this.unmappedItems.length) {
            filterRequest.variableNames = this.unmappedItems.map((mapItem) => mapItem.name);
        }
        this.variableService.filterVariables(filterRequest).toPromise().then(variables => {
            variables.forEach(variable => {
                const existing = this.unmappedItems.find(item =>
                    item.name.toLowerCase() === variable.name.toLowerCase() || //
                    // Allow matching by alias if it's not empty'
                    (variable.alias?.trim() !== '' && item.name.toLowerCase().includes(variable.alias.toLowerCase())));
                if (existing) {
                    this.mapItems.push({
                        id: variable.id,
                        name: variable.name,
                        variable: variable
                    });
                    this.unmappedItems = this.unmappedItems.filter(item => item.id !== existing.id);
                }
            });
        });

        if (this.importStudyContext.isEnvironmentsImportBoolean) {
            this.groupName = this.translateService.instant('study-editor.study-dataset.import-study-map.group-name.environment');
        } else if (this.importStudyContext.isStudySettingsImportBoolean) {
            this.groupName = this.translateService.instant('study-editor.study-dataset.import-study-map.group-name.study-settings');
        } else if (this.importStudyContext.isDesignDetailsImportBoolean) {
            this.groupName = this.translateService.instant('study-editor.study-dataset.import-study-map.group-name.design-details');
        } else {
            this.groupName = this.translateService.instant('study-editor.study-dataset.import-study-map.group-name.variate');
        }
    }

    saveMapping(): void {

        if (!this.isValid()) {
            return;
        }

        if (this.unmappedItems.length > 0) {
            const modalRef = this.modalService.open(ModalConfirmComponent);
            modalRef.componentInstance.title = this.translateService.instant('study-editor.study-dataset.import-study-map.has-unmapped-headers-confirm.title');
            modalRef.componentInstance.message = this.translateService.instant('study-editor.study-dataset.import-study-map.has-unmapped-headers-confirm');
            modalRef.result.then(() => {
                this.proceedToSave();
            });
        } else {
            this.proceedToSave();
        }
    }

    isValid() {
        if (this.mapItems.length === 0) {
            this.alertService.error('study-editor.study-dataset.import-study-map.no-mapped-items');
            return false;
        }
        if (this.mapItems.length > 0) {
            const hasNoMapping = this.mapItems.some(item => item.variable === null);
            if (hasNoMapping) {
                this.alertService.error('study-editor.study-dataset.import-study-map.has-no-mapping');
                return false;
            } else {
                return true;
            }
        }
        return false;
    }

    async proceedToSave() {

        this.isSaving = true;

        const existingVariablesResponse = await Promise.all(
            this.importStudyContext.variableTypeIds.map(variableTypeEnum =>
                this.datasetService.getDatasetVariablesByVariableType(
                    this.studyEditorContext.studyId,
                    this.importStudyContext.datasetId,
                    variableTypeEnum.valueOf()
                ).toPromise()
            )
        ).catch(error => {
            this.alertService.error('error.general');
            this.isSaving = false;
        });

        let existingVariables = [];

        if (existingVariablesResponse) {
            existingVariables = existingVariablesResponse
                .map(response => response.body?.map(variable => variable.name) || [])
                .reduce((acc, variableNames) => acc.concat(variableNames), []);
        }

        const addVariablesPromises: Promise<any>[] = [];

        this.mapItems.forEach(item => {
            const variable = item.variable;
            const variableTypeNames = variable.variableTypes.map(variableType => variableType.name);

            if (variable && !existingVariables.includes(item.name) && !existingVariables.includes(item.variable.name)) {
                let variableTypeId = this.resolveVariableTypeId(variableTypeNames);
                const idTranslations = {
                    // Collaborator
                    8373: 8372,
                    // PI Name
                    8100: 8110,
                };
                const variableId = idTranslations[variable.id] ? idTranslations[variable.id] : variable.id;
                const variablePromise = this.datasetService.addVariables(this.studyEditorContext.studyId, this.importStudyContext.datasetId, {
                    variableTypeId: variableTypeId,
                    variableId: variableId,
                    studyAlias: this.maintainHeaders ? item.name : item.variable.alias || item.variable.name,
                }).toPromise();

                if (this.importStudyContext.isDesignDetailsImportBoolean) {
                    variablePromise.then(result => {
                        const obsvaritem = result as ObservationVariableItem;
                        obsvaritem.deletable = true;
                        this.experimentalDesignContext.optionalDesignVariables.push(obsvaritem);
                    });
                }
                addVariablesPromises.push(variablePromise);

                if (!this.maintainHeaders) {
                    const headerIndex = this.importStudyContext.importedData[0].findIndex((name)=> {
                        return name === item.name;
                    });
                    this.importStudyContext.importedData[0][headerIndex] = item.variable.alias || item.variable.name;
                }
            }

        });

        Promise.all(addVariablesPromises).then(() => {
            this.activeModal.close('success');
            this.isSaving = false;
        }).finally(() => {
            this.isSaving = false;
        });
    }

    private resolveVariableTypeId(variableTypeNames: string[]) {
        if (this.importStudyContext.isEnvironmentsImportBoolean) {
            if (variableTypeNames.some(type => type.toLowerCase() === 'ENVIRONMENT DETAIL'.toLowerCase())) {
                return VariableTypeEnum.ENVIRONMENT_DETAIL.valueOf();
            } else {
                return VariableTypeEnum.ENVIRONMENT_CONDITION.valueOf();
            }
        } else if (this.importStudyContext.isStudySettingsImportBoolean) {
            return VariableTypeEnum.STUDY_DETAIL.valueOf();
        } else if (this.importStudyContext.isDesignDetailsImportBoolean) {
            return VariableTypeEnum.EXPERIMENTAL_DESIGN.valueOf();
        } else {
            if (variableTypeNames.some(type => type.toLowerCase() === 'TRAIT'.toLowerCase())) {
                return VariableTypeEnum.TRAIT.valueOf();
            } else {
                return VariableTypeEnum.SELECTION_METHOD.valueOf();
            }
        }
    }

    drop(event: CdkDragDrop<MapItem[]>): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );

            // If moving to variate group, add default properties
            if (event.container.id === 'mapped-group') {
                const item = event.container.data[event.currentIndex];
                if (item) {
                    item.variable = null;
                }
            }
        }
    }

    remapItem(item: MapItem): void {
        const modalRef = this.modalService.open(StudyVariableSelectModalComponent as Component, {
            modalDialogClass: 'modal-lg',
            backdrop: 'static'
        });
        modalRef.componentInstance.variableTypeIds = this.importStudyContext.variableTypeIds;
        modalRef.componentInstance.excludedVariableIds = this.importStudyContext.excludedVariableIds;
        modalRef.componentInstance.selectionType = 'single';
        //modalRef.existingVariableIdsInStudy = this.variables.map(v => v.termId);

        const subscription = modalRef.componentInstance.variableSelect.subscribe((variable) => {
            if (variable) {
                item.variable = variable;
            }
        });

        // Clean up when modal is closed
        modalRef.closed.subscribe(() => {
            if (subscription) {
                subscription.unsubscribe();
            }
        });
    }

    close() {
        this.activeModal.dismiss();
    }
}
